import React, { Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { observer } from 'mobx-react';

import { InjectedStyledComponent } from '../../../../../shared/index.js';
import { ITranslationContextValue } from '../../../Translate/models/ITranslationContextValue.js';
import Translate, { TranslationContext } from '../../../Translate/Translate.js';
import { TableComponentBase } from '../TableComponentBase.js';

import { ICellValueConfiguration, ICellValueProps, ICellValueState } from './models/index.js';
import {
	CELL_VALUE_CATEGORY_KEY,
	CELL_VALUE_COMPONENT_KEY,
	CELL_VALUE_PARENT_COMPONENT_KEY,
} from './CellValue.meta.js';

export class CellValue extends TableComponentBase<ICellValueProps, ICellValueState, ICellValueConfiguration> {
	categoryKey = CELL_VALUE_CATEGORY_KEY;
	componentKey = CELL_VALUE_COMPONENT_KEY;
	translationContext: ITranslationContextValue | undefined;

	get parentComponentKey(): string | undefined {
		return CELL_VALUE_PARENT_COMPONENT_KEY;
	}

	get tableEventsKey() {
		return undefined;
	}

	/**
	 * Translates a given value using a translation context.
	 * If the translation context is available, it checks the type of the value (number or string).
	 * If the value is a negative number or a string starting with a hyphen, it removes the hyphen and translates the value using a specific translation key.
	 * Otherwise, it returns the original value.
	 * @param value - The value to be translated.
	 * @returns The translated value.
	 */
	getTranslate(value: any, splitCharacters = false): string {
		if (splitCharacters && typeof value === 'string') {
			value = value.split('').join(' ');
		}

		const parsedValue = typeof value === 'number' ? Math.abs(value) : String(value).replace('-', '');
		const isNegative = typeof value === 'number' ? value < 0 : typeof value === 'string' && value.startsWith('-');

		if (isNegative) {
			// The translation context is defaulted in Application.tsx to (key) => key, exclamate to let ts know that it is always defined.
			return this.translationContext!.translate(`minus {value}`, { value: parsedValue });
		}

		return value;
	}

	constructor(props: ICellValueProps) {
		super(props);
	}

	render() {
		const { column, cellValue } = this.props;
		const { arraySeparator, splitCharacters } = column;

		return (
			<TranslationContext.Consumer>
				{(translationContext) => {
					this.translationContext = translationContext;
					if (!common.commonUtils.isUndefined(arraySeparator) && common.commonUtils.isArrayLike(cellValue)) {
						return (
							<Fragment>
								{cellValue.map((subValue, subIndex) => {
									return (
										<Fragment key={`${subIndex}_${subValue}`}>
											{subIndex > 0 && <span>{arraySeparator}</span>}
											<span aria-label={this.getTranslate(subValue, splitCharacters)}>
												<Translate>{subValue}</Translate>
											</span>
										</Fragment>
									);
								})}
							</Fragment>
						);
					}
					return (
						<span aria-label={this.getTranslate(cellValue, splitCharacters)}>
							<Translate>{cellValue}</Translate>
						</span>
					);
				}}
			</TranslationContext.Consumer>
		);
	}
}

export default InjectedStyledComponent(observer(CellValue), CELL_VALUE_CATEGORY_KEY, CELL_VALUE_PARENT_COMPONENT_KEY, [
	CELL_VALUE_COMPONENT_KEY,
]);
